import { ChangeEvent, DragEvent } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, CircularProgress } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';

import { useTrans } from "~/hooks/useTrans";
import { classNames } from "~/utils/utils";
import { IClientProjectCatalogGallery } from "~/providers/client-interfaces";
import { getImageDimensions } from "~/utils/image";
import { PREVIEW_MAX_FILE_SIZE_MB, PREVIEW_MAX_SIZE, PREVIEW_MIN_SIZE } from "~/const";

import "./style.less";

export interface IPreviewItem {
	id: string;
	// upload - fotka se da uploadovat
	// uploading - fotka se nahrava do swiftu
	// done - fotka je nahrana, ma data, da se presouvat
	state: "upload" | "uploading" | "done";
	imgSrc: string;
	data?: IClientProjectCatalogGallery;
}

export function createEmptyItem(): IPreviewItem {
	return {
		/* eslint-disable-next-line */
		id: Math.random().toString(16),
		imgSrc: "",
		state: "upload",
	};
}

/* eslint-disable-next-line */
const MB_BYTES = 1024 * 1024;

interface IPreviewItemProps {
	data: IPreviewItem;
	firstItem?: boolean;
	onUpload?: (file: File) => void;
	onError?: (msg: string) => void;
	onRemove?: () => void;
}

export default function PreviewItem({
	data,
	firstItem = false,
	onUpload = () => {},
	onError = () => {},
	onRemove = () => {},
}: IPreviewItemProps) {
	const { trans } = useTrans();
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: data.id });
	const style = {
		transition,
		transform: CSS.Transform.toString(transform),
	};

	async function processFile(file: File) {
		if (file.type.match("image")) {
			const maxFileSize = PREVIEW_MAX_FILE_SIZE_MB * MB_BYTES;

			if (file.size >= maxFileSize) {
				const fileMb = (file.size / MB_BYTES).toFixed(1);

				onError(trans("editProjectCatalogGalleryModal.error.wrongFileSize", {
					size: fileMb,
					rightSize: PREVIEW_MAX_FILE_SIZE_MB,
				}));

				return;
			}

			const dimension = await getImageDimensions(file);

			if (dimension.width < PREVIEW_MIN_SIZE || dimension.width > PREVIEW_MAX_SIZE || dimension.height < PREVIEW_MIN_SIZE || dimension.height > PREVIEW_MAX_SIZE) {
				onError(trans("editProjectCatalogGalleryModal.error.wrongSize", {
					picWidth: dimension.width,
					picHeight: dimension.height,
					minSize: PREVIEW_MIN_SIZE,
					maxSize: PREVIEW_MAX_SIZE,
				}));
			} else {
				onUpload(file);
			}
		} else {
			onError(trans("editProjectCatalogGalleryModal.error.wrongExt"));
		}
	}

	function onChange(event: ChangeEvent<HTMLInputElement>) {
		const files = Array.from(event.target.files);

		event.target.value = null;

		if (files.length) {
			const file = files[0];

			processFile(file);
		}
	}

	function onDragStart(event: DragEvent<HTMLDivElement>) {
		event.stopPropagation();
		event.preventDefault();
	}

	function onDrop(event: DragEvent<HTMLDivElement>) {
		event.stopPropagation();
		event.preventDefault();

		if (event.dataTransfer && event.dataTransfer.files) {
			processFile(event.dataTransfer.files[0]);
		}
	}

	return <div className={classNames(["previewItem", firstItem ? "first-item" : ""])} style={style} data-state={data.state}>
		{ data.state === "done" && <Button onClick={onRemove} className="previewItem__removeBtn">
			<DeleteIcon sx={{ fill: "#fff" }} />
		</Button> }
		{ data.state === "done" && <div className="previewItem__done" ref={setNodeRef} {...attributes} {...listeners}>
			<div className="previewItem__imgCover">
				<img src={data.imgSrc} alt="" className="previewItem__doneCover" />
			</div>
		</div> }
		{ data.state === "upload" && <div className="previewItem__upload" onDrop={onDrop} onDragOver={onDragStart} onDragStart={onDragStart}>
			<label className="previewItem__uploadLabel">
				<span className="previewItem__uploadLabelText">
					{ trans("editProjectCatalogGalleryModal.upload") }
				</span>
				<input className="previewItem__uploadInput" type="file" multiple={false} accept="image/*" onChange={onChange} />
			</label>
		</div> }
		{ data.state === "uploading" && <div className="previewItem__uploading">
			<CircularProgress />
		</div> }
	</div>;
}
